import { FC, useCallback, useEffect, useState } from 'react';
import cfi from 'country-flag-icons/react/3x2';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import camelCase from 'lodash/camelCase';
import { useAppTranslation } from '../../i18n';
import { DomainInfoPanelLanguage } from '../../store/api/auth-api';
import { useAppSelector } from '../../store';

const flagStyle = { width: '30px', height: '20px' } as const;

export const getFlag = (code: string, name?: string): JSX.Element => {
  const title = name || code;
  // special cases
  switch (code.toLowerCase()) {
    case 'en':
    case 'en-uk':
    case 'gb':
      return <cfi.GB title={title} style={flagStyle} />;
    case 'au':
    case 'en-au':
      return <cfi.AU title={title} style={flagStyle} />;
    case 'us':
    case 'en-us':
      return <cfi.US title={title} style={flagStyle} />;
    case 'el':
      return <cfi.GR title={title} style={flagStyle} />;
  }
  // other English variations
  if (code.toLowerCase().startsWith('en')) {
    return <cfi.GB title={title} style={flagStyle} />;
  }
  // as a fallback, just a flag
  const FlagComponent: cfi.FlagComponent | null = (cfi as any)[code];
  if (FlagComponent) {
    return FlagComponent({ title, style: flagStyle });
  }
  // if code not found in the lib, return EU flag
  return <cfi.EU title={title} style={flagStyle} />;
};

export const LanguagePicker: FC = () => {
  const { t: lt, i18n } = useAppTranslation('languages');
  const domainInfo = useAppSelector((s) => s.domainInfo);
  const [languages, setLanguages] = useState<DomainInfoPanelLanguage[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const onLanguageChange = useCallback(
    (language: string) => {
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    },
    [i18n]
  );

  const onChange = useCallback(
    (e: SelectChangeEvent) => {
      onLanguageChange(e.target.value);
    },
    [onLanguageChange]
  );

  useEffect(() => {
    if (domainInfo && !isEmpty(domainInfo)) {
      let langs: DomainInfoPanelLanguage[] = [];
      for (const p of domainInfo.panels) {
        for (const l of p.languages) {
          if (!langs.some((x) => x.language_code === l.language_code)) {
            langs.push(l);
          }
        }
      }
      langs = sortBy(langs, (l) => l.language_name);
      setLanguages(langs);
      setLoaded(true);
    }
  }, [domainInfo]);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    if (languages.length === 0) {
      onLanguageChange('en');
    }
    if (languages.length === 1) {
      onLanguageChange(languages[0].language_code);
    }
    if (languages.length > 1 && !languages.some((l) => l.language_code === i18n.language)) {
      onLanguageChange(
        languages.find((l) => l.is_default)?.language_code ??
          languages.find((l) => l.language_code.startsWith('en'))?.language_code ??
          languages[0].language_code
      );
    }
  }, [onLanguageChange, i18n, loaded, languages]);

  if (languages.length <= 1) {
    return null;
  }
  return (
    <Select
      id="select-language"
      variant="standard"
      renderValue={(value) => getFlag(value, languages.find((l) => l.language_code === value)?.language_name)}
      value={i18n.language}
      onChange={onChange}
      sx={{ background: 'none' }}
    >
      {languages.map((l) => (
        <MenuItem key={l.language_code} value={l.language_code}>
          {getFlag(l.language_code, l.language_name)}&nbsp;{lt(camelCase(l.language_name), l.language_name)}
        </MenuItem>
      ))}
    </Select>
  );
};
